import { InjectionToken } from '@angular/core';
import { ApplicationIdentifier, PaceBarcode } from '@evorait/pace-components';
import { Observable } from 'rxjs';
import { BarcodeScanResult } from '../services/barcode-scanner/barcode-scanner.const';

/**
 * Public contract of the barcode scanner service: opening the scan modal, plus the small
 * barcode clipboard (a capped, persisted list of recently scanned/entered barcodes) the modal
 * offers as quick-pick candidates.
 *
 * Consumers depend on this interface via {@link BarcodeScannerInstanceToken} rather than on a
 * concrete class, so the implementation behind it can be swapped without touching them.
 */
export interface BarcodeScannerInterface {
    /** The current barcode clipboard, most-recent first, capped at the retention policy. */
    readonly barcodeClipboard$: Observable<PaceBarcode[]>;

    /**
     * Opens the barcode scanner modal and returns its scan result.
     *
     * @param pattern an optional pattern the scanned/entered text is expected to match
     * @param appId the application identifier the scan is performed for; defaults to
     * `ConfigConstants.ApplicationIdentifier.Default`
     * @param clipboardAccessible whether the clipboard of previously scanned barcodes may be
     * opened from the modal; defaults to `true`
     * @returns an observable emitting the scan result once the modal completes
     */
    scan(pattern?: string, appId?: ApplicationIdentifier, clipboardAccessible?: boolean): Observable<BarcodeScanResult>;

    /**
     * Replaces the barcode clipboard with the given collection (capped at the retention
     * policy) and persists it.
     *
     * @param barcodes the new clipboard contents
     * @returns an observable emitting `true` once persisted, `false` on failure
     */
    setBarcodeClipboard(barcodes: PaceBarcode[]): Observable<boolean>;

    /**
     * Persists the given barcode clipboard to the shared concurrent-usage storage so other
     * devices/sessions can pick it up.
     *
     * @param barcodes the clipboard contents to upload
     * @returns an observable emitting `true` once persisted, `false` on failure
     */
    uploadBarcodeClipboard(barcodes: PaceBarcode[]): Observable<boolean>;

    /**
     * Refreshes the barcode clipboard from storage.
     *
     * @returns an observable emitting `true` once the clipboard has been refreshed
     */
    fetchBarcodeClipboard(): Observable<boolean>;
}

/**
 * DI token consumers use to resolve a {@link BarcodeScannerInterface} implementation. Which
 * provider each app module binds to it is decided by the `enableNgRx` environment flag.
 */
export const BarcodeScannerInstanceToken = new InjectionToken<BarcodeScannerInterface>('BarcodeScannerInterface');
